import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '../../utils/ThemeContext';
import classes from './GameTickets.module.scss';

function GameTickets({ nightMode }) {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [, setIsMobile] = useState(false);

  const resultPick3 = useSelector((state) => state.game.resultPick3);
  const resultLotto_5_90 = useSelector((state) => state.game.resultLotto_5_90);
  const resultLotto_4_48 = useSelector((state) => state.game.resultLotto_4_48);
  const resultLotto_6_49 = useSelector((state) => state.game.resultLotto_6_49);

  const [ticketsByGame, setTicketsByGame] = useState({});
  const [, setCurrentDateTime] = useState('');
  const [generatedNumbers, setGeneratedNumbers] = useState({});

  const handleResize = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getCustomCircleStyle = (gameName) => {
    if (!gameName) return { circleClass: classes.defaultCircle, text: [] };
    const gameNameLower = gameName.toLowerCase();
    switch (gameNameLower) {
      case 'pick 3':
        return { circleClass: classes.pick3Circle, text: ['3'] };
      case 'lotto 5/90':
        return { circleClass: classes.lotto590Circle, text: ['5', '90'] };
      case 'lotto 4/48':
        return { circleClass: classes.lotto448Circle, text: ['4', '48'] };
      case 'lotto 6/49':
        return { circleClass: classes.lotto649Circle, text: ['6', '49'] };
      default:
        return { circleClass: classes.defaultCircle, text: [] };
    }
  };

  const returnResult = (gameName) => {
    switch (gameName) {
      case 'pick 3':
        return resultPick3;
      case 'lotto 5/90':
        return resultLotto_5_90;
      case 'lotto 4/48':
        return resultLotto_4_48;
      case 'Lotto 6/49':
        return resultLotto_6_49;
      default:
        return resultPick3;
    }
  };

  const getNumberColor = (number) => {
    const lastDigit = number % 10;
    switch (lastDigit) {
      case 0:
        return '#BE302B';
      case 1:
        return '#962CB8';
      case 2:
        return '#5BBA2F';
      case 3:
        return '#31B6C2';
      case 4:
        return '#3274C5';
      case 5:
        return '#B92DBB';
      case 6:
        return '#BDA02F';
      case 7:
        return '#BF7E37';
      case 8:
        return '#585858';
      case 9:
        return '#18962E';
      default:
        return 'defaultColor';
    }
  };

  const getFormattedDateTime = (time) => {
    const now = new Date(time);

    let hours = now.getHours();
    if (hours === 22 || hours === 23) {
      hours = 10;
    }

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    return `${month}/${day}/${year} ${String(hours).padStart(2, '0')}:${minutes}`;
  };

  useEffect(() => {
    const currentTime = Date.now();
    setCurrentDateTime(getFormattedDateTime(currentTime));

    if (theme.themeName) {
      const newResults = returnResult(theme.themeName) || [];
      if (newResults.length > 0) {
        setTicketsByGame((prevTickets) => {
          const currentTickets = prevTickets[theme.themeName] || [];
          const staticTickets = Array(6)
            .fill()
            .map(() => generateRandomNumbers(theme.themeName));
          const randomId = Math.floor(1000 + Math.random() * 9000);
          const updatedTickets = {
            ...prevTickets,
            [theme.themeName]: [
              {
                game: theme.themeName,
                results: newResults,
                ticketId: randomId,
                ticketTime: currentTime,
              },
              ...currentTickets,
              ...staticTickets.map((ticket, idx) => ({
                game: theme.themeName,
                results: ticket,
                ticketId: Math.floor(1000 + Math.random() * 9000),
                ticketTime: currentTime,
              })),
            ].slice(0, 6),
          };

          return updatedTickets;
        });
      }
    }
  }, [resultPick3, resultLotto_5_90, resultLotto_4_48, resultLotto_6_49]);

  const ticketsToShow = ticketsByGame[theme.themeName] || [];

  const generateRandomNumbers = (gameName) => {
    const getRandomNumbers = (count, min, max) => {
      let numbers = new Set();
      while (numbers.size < count) {
        numbers.add(Math.floor(Math.random() * (max - min + 1)) + min);
      }
      return [...numbers];
    };

    switch (gameName) {
      case 'pick 3':
        return getRandomNumbers(3, 0, 9);
      case 'lotto 5/90':
        return getRandomNumbers(5, 1, 90);
      case 'lotto 4/48':
        return getRandomNumbers(4, 1, 48);
      case 'lotto 6/49':
        return getRandomNumbers(6, 1, 49);
      default:
        return [7, 14, 21, 28, 35];
    }
  };

  useEffect(() => {
    if (!generatedNumbers[theme.themeName]) {
      setGeneratedNumbers((prev) => ({
        ...prev,
        [theme.themeName]: Array(6)
          .fill()
          .map(() => generateRandomNumbers(theme.themeName)),
      }));
    }
  }, [theme.themeName]);

  return (
    <>
      <div
        className={`${classes.betsSpecialMobile} ${isOpen ? classes.open : ''}`}
        style={{
          background: nightMode ? theme.selectedColor : theme.backgroundInside,
        }}
      >
        <span
          className={`${classes.gameList_title}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          Current Results
        </span>
      </div>

      <div
        className={`${classes.GameTickets} ${isOpen ? classes.open : classes.closed}`}
        style={{
          background: nightMode ? theme.selectedColor : theme.backgroundInside,
        }}
      >
        {ticketsToShow.length > 0 ? (
          <div className={classes.drawContainer}>
            {ticketsToShow.map((ticket, index) => {
              const { circleClass, text } = getCustomCircleStyle(ticket.game);
              return (
                <div key={index} className={classes.ticketWrapper}>
                  <div
                    className={classes.ticketStyle}
                    style={{ backgroundColor: theme?.backgroundTicket }}
                  >
                    <div className={classes.titlesCont}>
                      <div className={classes.middleCont}>
                        <span
                          className={`${classes.titleStyle} ${classes.dateTitle}`}
                        >
                          Date & Time :
                        </span>
                        <span
                          className={`${classes.titleStyle} ${classes.dateSubTitle}`}
                        >
                          {getFormattedDateTime(ticket.ticketTime)}
                        </span>
                      </div>
                      <div className={classes.middleCont}>
                        <span
                          className={`${classes.titleStyle} ${classes.dateTitle}`}
                        >
                          Draw ID : #{ticket.ticketId}
                        </span>
                      </div>
                    </div>
                    <div className={classes.ballsToShowStyle}>
                      {ticket.results.map((number, idx) => {
                        const numberColor = getNumberColor(number);
                        return (
                          <div
                            key={idx}
                            className={`${classes.circle}`}
                            style={{ backgroundColor: numberColor }}
                          >
                            <div
                              className={`${classes.circleInner}`}
                              style={{ backgroundColor: numberColor }}
                            >
                              <span className={classes.textInsideCircle}>
                                {number}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          Array.from({ length: 6 }).map((_, idx) => {
            const randomNumbers =
              generatedNumbers[theme.themeName]?.[idx] || [];
            const { circleClass, text } = getCustomCircleStyle(theme.themeName);
            return (
              <div key={idx} className={classes.ticketWrapper}>
                {/* <div
                  className={`${classes.gameLogo} ${circleClass}`}
                  style={{ background: theme.ballsColor }}
                >
                  {text.map((item, idx) => (
                    <span
                      key={idx}
                      className={
                        text.length === 1
                          ? classes.singleItemStyle
                          : idx === 0
                            ? classes.firstItemStyle
                            : classes.secondItemStyle
                      }
                    >
                      {item}
                    </span>
                  ))}
                </div> */}
                <div
                  className={classes.ticketStyle}
                  style={{ backgroundColor: theme?.backgroundTicket }}
                >
                  <div className={classes.titlesCont}>
                    <div className={classes.middleCont}>
                      <span
                        className={`${classes.titleStyle} ${classes.dateTitle}`}
                      >
                        Date & Time : <span
                          className={`${classes.titleStyle} ${classes.dateSubTitle}`}
                        >
                          {getFormattedDateTime(Date.now())}
                        </span>
                      </span>
                    </div>
                    <div className={classes.middleCont}>
                      <span
                        className={`${classes.titleStyle} ${classes.dateTitle}`}
                      >
                        Draw ID #{Math.floor(1000 + Math.random() * 9000)}
                      </span>
                    </div>
                  </div>
                  <div className={classes.ballsToShowStyle}>
                    {randomNumbers.map((number, idx) => {
                      const numberColor = getNumberColor(number);
                      return (
                        <div
                          key={idx}
                          className={`${classes.circle}`}
                          style={{ backgroundColor: numberColor }}
                        >
                          <div
                            className={`${classes.circleInner}`}
                            style={{ backgroundColor: numberColor }}
                          >
                            <span className={classes.textInsideCircle}>
                              {number}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

export default GameTickets;
